import React, { useEffect, useRef, useState } from "react";
import { router } from "expo-router";
import { useSetAtom } from "jotai/react";
import { CircleCheckBigIcon } from "lucide-react-native";
import { toast } from "sonner-native";

import { ApplicationProgramConflictDialog } from "@/blocks/ApplicationProgramConflictDialog";
import { RecentDuplicateApplicationsDialog } from "@/blocks/RecentDuplicateApplicationsDialog";
import { VnBox } from "@/components/VnBox";
import { VnButton } from "@/components/VnButton/VnButton";
import { VnIcon } from "@/components/VnIcon";
import { VnLink } from "@/components/VnLink";
import { VnText } from "@/components/VnText/VnText";
import { useRecentDuplicateApplicationsQuery } from "@/data/queries/applicationsQueries";
import { usePendingConsentsQuery } from "@/data/queries/consentsQueries";
import {
  APPLICATION_FLOW_DEFAULT_STEPS,
  CreateApplicationType,
  RecentDuplicateApplicationSelectionType,
  RecentDuplicateApplicationType,
} from "@/domain/applications";
import { useApplicationForm } from "@/hooks/useApplicationForm";
import { useTranslator } from "@/hooks/useTranslate";
import { ApplicationFormAccordions } from "@/interactors/CreateApplicationForm";
import { applicationTotalStepsAtom } from "@/stores/atoms";

type ApplicationFormPropsType = {
  formType: "eligibilityResults" | "applicationStart";
};

type ApplicationCreationStatusType =
  | "idle"
  | "processing"
  | "awaitingDuplicateConfirmation";

export const ApplicationForm = ({ formType }: ApplicationFormPropsType) => {
  const { t } = useTranslator();
  const setApplicationTotalSteps = useSetAtom(applicationTotalStepsAtom);
  const { refetch: refetchPendingConsents } = usePendingConsentsQuery({
    enabled: false,
  });

  const applicationForms = useApplicationForm({
    onSubmit: (_, applicationTypes) => {
      const shouldRequireBrokerConsent =
        pendingConsentsRef.current?.includes("BROKER_HANDOFF") &&
        applicationTypes.some(t => t === "MEDICARE" || t === "MARKETPLACE");

      if (shouldRequireBrokerConsent) {
        setApplicationTotalSteps(APPLICATION_FLOW_DEFAULT_STEPS + 1);
        router.navigate({
          pathname: "/(authed)/(modal)/application/broker-consent",
        });
      } else {
        setApplicationTotalSteps(APPLICATION_FLOW_DEFAULT_STEPS);
        router.navigate({
          pathname: "/(authed)/(modal)/application/citizenship-status",
        });
      }
    },
  });

  const [recentDuplicateApplications, setRecentDuplicateApplications] =
    useState<RecentDuplicateApplicationType[]>([]);
  const { fetchRecentDuplicateApplications } =
    useRecentDuplicateApplicationsQuery();
  const [applicationCreationStatus, setApplicationCreationStatus] =
    useState<ApplicationCreationStatusType>("idle");
  const pendingApplicationPayloadsRef = useRef<
    readonly CreateApplicationType[] | null
  >(null);
  const isApplicationCreationLockedRef = useRef(false);
  const isContinuingAnywayRef = useRef(false);

  const isApplicationCreationLocked = applicationCreationStatus !== "idle";
  const isApplicationCreationProcessing =
    applicationCreationStatus === "processing" || applicationForms.isLoading;

  // Ref holds the fetched pending consents so onSubmit can read them without stale closure issues.
  // We prefetch before creating applications to avoid bypassing broker consent on refetch failure.
  const pendingConsentsRef = useRef<string[]>([]);

  const resetApplicationCreationFlow = () => {
    pendingApplicationPayloadsRef.current = null;
    isApplicationCreationLockedRef.current = false;
    isContinuingAnywayRef.current = false;
    setRecentDuplicateApplications([]);
    setApplicationCreationStatus("idle");
  };

  const continueApplicationCreation = async (
    applicationPayloads: readonly CreateApplicationType[],
  ) => {
    const { data: pendingConsents } = await refetchPendingConsents();

    if (!pendingConsents) {
      toast.error(t("common:errors.unknown"));
      return;
    }

    pendingConsentsRef.current = pendingConsents.pendingConsents;
    await applicationForms.handleSubmit(applicationPayloads);
  };

  const handleContinue = async () => {
    if (isApplicationCreationLockedRef.current) return;

    const applicationPayloads = applicationForms.prepareApplicationPayloads();
    if (applicationPayloads.length === 0) return;

    const selectedApplications = applicationPayloads.map(application => ({
      applicationType: application.applicationType,
      householdMemberIds: application.householdMembers.map(
        member => member.memberId,
      ),
    })) satisfies RecentDuplicateApplicationSelectionType[];

    isApplicationCreationLockedRef.current = true;
    pendingApplicationPayloadsRef.current = applicationPayloads;
    setApplicationCreationStatus("processing");

    try {
      const recentDuplicates =
        await fetchRecentDuplicateApplications(selectedApplications);

      if (!recentDuplicates) {
        toast.error(t("common:errors.unknown"));
        resetApplicationCreationFlow();
        return;
      }

      if (recentDuplicates.length > 0) {
        setRecentDuplicateApplications(recentDuplicates);
        setApplicationCreationStatus("awaitingDuplicateConfirmation");
        return;
      }

      await continueApplicationCreation(applicationPayloads);
      resetApplicationCreationFlow();
    } catch {
      toast.error(t("common:errors.unknown"));
      resetApplicationCreationFlow();
    }
  };

  const handleContinueAnyway = async () => {
    if (isContinuingAnywayRef.current) return;

    const applicationPayloads = pendingApplicationPayloadsRef.current;
    if (!applicationPayloads) {
      resetApplicationCreationFlow();
      return;
    }

    isContinuingAnywayRef.current = true;
    setRecentDuplicateApplications([]);
    setApplicationCreationStatus("processing");

    try {
      await continueApplicationCreation(applicationPayloads);
    } catch {
      toast.error(t("common:errors.unknown"));
    } finally {
      resetApplicationCreationFlow();
    }
  };

  const handleDuplicateDialogClose = () => {
    if (isContinuingAnywayRef.current) return;
    resetApplicationCreationFlow();
  };

  // Lazy initializers capture values immediately if already available (e.g. from query cache)
  const [originalEligibleProgramsCount, setOriginalEligibleProgramsCount] =
    useState(applicationForms.eligibleProgramsCount || 0);
  const [originalHouseholdMembersCount, setOriginalHouseholdMembersCount] =
    useState(applicationForms.uniqueHouseholdMembersCount || 0);

  // Capture the first nons-zero values since data may not be available on the initial render.
  // Uses state (not refs) so the update triggers a re-render with the correct values.
  // Runs after commit to avoid mutating refs during render in concurrent React.
  useEffect(() => {
    if (
      !originalEligibleProgramsCount &&
      applicationForms.eligibleProgramsCount
    ) {
      setOriginalEligibleProgramsCount(applicationForms.eligibleProgramsCount);
    }
    if (
      !originalHouseholdMembersCount &&
      applicationForms.uniqueHouseholdMembersCount
    ) {
      setOriginalHouseholdMembersCount(
        applicationForms.uniqueHouseholdMembersCount,
      );
    }
  }, [
    originalEligibleProgramsCount,
    applicationForms.eligibleProgramsCount,
    originalHouseholdMembersCount,
    applicationForms.uniqueHouseholdMembersCount,
  ]);

  return (
    <VnBox className="mx-auto gap-6 md:w-[480px]">
      <VnBox className="mb-6 items-center gap-3">
        <VnBox className="bg-chart-five rounded-full p-2">
          <VnIcon as={CircleCheckBigIcon} size={32} className="text-white" />
        </VnBox>
        <VnText
          i18nKey="native:applicationForm.greatNews"
          variant="headerOne"
        />
        <VnBox className="items-center">
          <VnText
            variant="lg"
            className="text-muted-foreground text-center"
            i18nKey="native:applicationForm.programsFound"
            i18nOptions={{
              programsCount: originalEligibleProgramsCount,
              householdMembersCount: originalHouseholdMembersCount,
            }}
          />
          <VnText
            variant="lg"
            className="text-muted-foreground text-center"
            i18nKey="native:applicationForm.letsCreateApplications"
          />
        </VnBox>
      </VnBox>
      <ApplicationFormAccordions
        medicaidForm={applicationForms.forms.medicaid}
        medicareForm={applicationForms.forms.medicare}
        marketplaceForm={applicationForms.forms.marketplace}
        openAccordions={applicationForms.openAccordions}
        handleValueChange={applicationForms.setOpenAccordions}
        isDisabled={isApplicationCreationLocked}
        onRequestMemberSelection={applicationForms.handleMemberSelectionRequest}
      />
      <VnText
        className="text-center"
        i18nKey="native:applicationForm.applyingToCounter"
        i18nOptions={{
          programsCount: t("common:program", {
            count: applicationForms.selectedProgramsCount,
          }),
          householdMembersCount: t("common:member", {
            count: applicationForms.uniqueHouseholdMembersCount,
          }),
        }}
      />
      <VnButton
        i18nKey={
          formType === "eligibilityResults"
            ? "native:applicationForm.continueButton"
            : "native:applicationForm.startButton"
        }
        disabled={
          applicationForms.isContinueDisabled || isApplicationCreationLocked
        }
        onPress={() => {
          void handleContinue();
        }}
        isLoading={isApplicationCreationProcessing}
      />
      {formType === "eligibilityResults" && (
        <VnLink
          href={{
            pathname: "/(authed)",
          }}
          i18nKey="common:returnToDashboard"
          renderAsButton
          isDisabled={isApplicationCreationLocked}
          buttonProps={{
            variant: "ghost",
          }}
        />
      )}
      <ApplicationProgramConflictDialog
        conflict={applicationForms.pendingProgramConflict}
        onKeep={applicationForms.handleKeepProgram}
        onSwitch={applicationForms.handleSwitchProgram}
      />
      <RecentDuplicateApplicationsDialog
        applications={recentDuplicateApplications}
        onClose={handleDuplicateDialogClose}
        onContinue={() => {
          void handleContinueAnyway();
        }}
      />
    </VnBox>
  );
};
